import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { brand } from "@gsb-core/brand";
import {
  Archive,
  BookOpen,
  Bot,
  ClipboardList,
  Code2,
  Database,
  FileCode2,
  FileText,
  Globe2,
  HardDrive,
  KeyRound,
  LayoutDashboard,
  List,
  LogOut,
  Moon,
  RefreshCw,
  Rows3,
  ScrollText,
  Settings,
  Mail,
  ShieldCheck,
  Sparkles,
  Table2,
  Terminal,
  Trash2,
  UserCog,
  Users,
  Workflow,
} from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@gsb-core/react-components";
import { useSession } from "@gsb-core/react-auth";
import { EntityDefService, type GsbEntityDef } from "@gsb-core/core";
import {
  AppShell,
  BrandMark,
  CommandPalette,
  KeyboardShortcutsDialog,
  ThemeMenu,
  useRegisterCommands,
  useRegisterCommandSource,
  useTheme,
  type CommandAction,
  type CommandSource,
  type NavItem,
  type NavSection,
  type UiLinkComponent,
} from "@gsb-core/react-ui";
import {
  AVAILABLE_LANGUAGES,
  getFullLocaleCode,
  useTranslation,
} from "@gsb-core/react-multilang";
import { stripLanguagePrefix, withLanguagePrefix } from "../language-path";
import { RouteIdentityProvider } from "../route-identity";
import type {
  WorkspaceRouteManifestEntry,
  WorkspaceRouteShell,
} from "../gsb-app";

const RouterLink: UiLinkComponent = ({ href, children, ...rest }) => {
  const { pathname } = useLocation();
  return (
    <Link to={withLanguagePrefix(href, pathname)} {...rest}>
      {children}
    </Link>
  );
};

const dashboardSections: readonly NavSection[] = [
  {
    id: "overview",
    items: [
      {
        href: "/dashboard",
        label: "Overview",
        icon: LayoutDashboard,
        exact: true,
      },
    ],
  },
  {
    id: "data",
    label: "Data",
    items: [
      { href: "/dashboard/database", label: "Database", icon: Database },
      {
        href: "/dashboard/system/selectlists",
        label: "Select lists",
        icon: List,
      },
      { href: "/dashboard/storage", label: "Storage", icon: HardDrive },
      {
        href: "/dashboard/storage?trash=1",
        label: "Trash",
        icon: Trash2,
      },
      {
        href: "/dashboard/system/doctemplates",
        label: "Document templates",
        icon: FileText,
      },
    ],
  },
  {
    id: "logic",
    label: "Logic",
    items: [
      { href: "/dashboard/functions", label: "Functions", icon: FileCode2 },
      { href: "/dashboard/workflow", label: "Workflows", icon: Workflow },
      {
        href: "/dashboard/system/recurringjobs",
        label: "Recurring jobs",
        icon: RefreshCw,
      },
      {
        href: "/dashboard/system/codegenerator",
        label: "Code generators",
        icon: Code2,
      },
    ],
  },
  {
    id: "access",
    label: "Access",
    items: [
      { href: "/dashboard/authentication/users", label: "Users", icon: Users },
      {
        href: "/dashboard/authentication/roles",
        label: "Roles",
        icon: ShieldCheck,
      },
      {
        href: "/dashboard/authentication/groups",
        label: "Groups",
        icon: UserCog,
      },
      {
        href: "/dashboard/authentication/permissions",
        label: "Permissions",
        icon: KeyRound,
      },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    items: [
      {
        href: "/dashboard/system/systemlogs",
        label: "System logs",
        icon: ScrollText,
      },
      {
        href: "/dashboard/system/auditlogs",
        label: "Audit logs",
        icon: ClipboardList,
      },
      { href: "/dashboard/system/backups", label: "Backups", icon: Archive },
    ],
  },
  {
    id: "tools",
    label: "Tools",
    items: [
      { href: "/dashboard/api-console", label: "API console", icon: Terminal },
      { href: "/dashboard/assistant", label: "Assistant", icon: Bot },
    ],
  },
  {
    id: "configure",
    label: "Configure",
    items: [
      {
        href: "/dashboard/settings",
        label: "Workspace settings",
        icon: Settings,
        children: [
          {
            href: "/dashboard/settings/general",
            label: "General settings",
            icon: Settings,
          },
          {
            href: "/dashboard/settings/security",
            label: "Security settings",
            icon: ShieldCheck,
          },
          {
            href: "/dashboard/settings/modules",
            label: "Module settings",
            icon: Code2,
          },
          {
            href: "/dashboard/settings/smtp",
            label: "SMTP settings",
            icon: Mail,
          },
          {
            href: "/dashboard/settings/team",
            label: "Workspace team",
            icon: Users,
          },
          {
            href: "/dashboard/settings/domains",
            label: "Domains",
            icon: Globe2,
          },
        ],
      },
    ],
  },
];

/** Old menu names and colloquial terms still have to find the screen that replaced them. */
const navSearchAliases: Record<string, readonly string[]> = {
  "/dashboard/system/selectlists": ["system", "enum", "lookup", "options"],
  "/dashboard/system/doctemplates": ["system", "documents", "reports", "pdf"],
  "/dashboard/system/recurringjobs": ["system", "cron", "schedule", "jobs"],
  "/dashboard/system/codegenerator": ["system", "scaffold", "generator"],
  "/dashboard/system/systemlogs": ["system", "errors", "events", "logs"],
  "/dashboard/system/auditlogs": ["system", "history", "trail", "logs"],
  "/dashboard/system/backups": ["system", "restore", "snapshot"],
  "/dashboard/authentication/users": ["authentication", "identity", "accounts"],
  "/dashboard/authentication/roles": ["authentication", "identity", "access"],
  "/dashboard/authentication/groups": ["authentication", "identity", "teams"],
  "/dashboard/authentication/permissions": [
    "authentication",
    "identity",
    "grants",
    "acl",
  ],
  "/dashboard/api-console": ["rest", "request", "query", "endpoint"],
  "/dashboard/workflow": ["workflow", "process", "designer"],
  "/dashboard/functions": ["serverless", "code", "lambda"],
  "/dashboard/storage": ["files", "uploads", "documents"],
  "/dashboard/database": ["entities", "tables", "rows", "schema"],
};

function routeMatchesPath(pathname: string, routePath: string): boolean {
  const routeParts = routePath.split("/").filter(Boolean);
  const pathParts = pathname.split("/").filter(Boolean);
  return (
    routeParts.length === pathParts.length &&
    routeParts.every(
      (part, index) => part.startsWith(":") || part === pathParts[index],
    )
  );
}

function getRouteIdentity(
  pathname: string,
  manifest: readonly WorkspaceRouteManifestEntry[],
): string {
  const match = [...manifest]
    .filter((route) => routeMatchesPath(pathname, route.path))
    .sort((first, second) => second.path.length - first.path.length)[0];
  return match?.identity ?? "Workspace";
}

function getLanguage(pathname: string): { code: string; dir: "ltr" | "rtl" } {
  const firstSegment = pathname.split("/").filter(Boolean)[0];
  const language =
    AVAILABLE_LANGUAGES[getFullLocaleCode(firstSegment ?? "en-US")];
  return { code: language.code, dir: language.isRTL ? "rtl" : "ltr" };
}

function flattenNavItems(items: readonly NavItem[]): readonly NavItem[] {
  return items.flatMap((item) => [
    item,
    ...flattenNavItems(item.children ?? []),
  ]);
}

function localizeSections(
  sections: readonly NavSection[],
  pathname: string,
): readonly NavSection[] {
  return sections.map((section) => ({
    ...section,
    items: localizeItems(section.items, pathname),
  }));
}

function localizeItems(
  items: readonly NavItem[],
  pathname: string,
): readonly NavItem[] {
  return items.map((item) => ({
    ...item,
    href: withLanguagePrefix(item.href, pathname),
    children:
      item.children === undefined
        ? undefined
        : localizeItems(item.children, pathname),
  }));
}

function WorkspaceAccountMenu({ pathname }: { readonly pathname: string }) {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState(false);
  const displayName = user?.displayName ?? user?.email ?? "Account";
  const accountInitial = displayName.charAt(0).toUpperCase();
  const localize = (path: string) => withLanguagePrefix(path, pathname);

  const handleLogout = async () => {
    setLoggingOut(true);
    setLogoutError(false);
    try {
      await logout();
      await navigate(localize("/login"), { replace: true });
    } catch {
      setLogoutError(true);
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Account menu for ${displayName}`}
          disabled={loggingOut}
          className="rounded-full"
        >
          <span
            aria-hidden
            className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-subtle text-caption font-semibold text-content-brand"
          >
            {accountInitial}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="normal-case">
          <span className="block truncate text-label text-content-primary">
            {displayName}
          </span>
          {user?.email ? (
            <span className="block truncate text-caption font-normal text-content-tertiary">
              {user.email}
            </span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() =>
            void navigate(localize("/dashboard/settings/security"))
          }
          className="gap-2"
        >
          <KeyRound aria-hidden className="h-4 w-4 text-content-tertiary" />
          Change password
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => void handleLogout()}
          disabled={loggingOut}
          className="gap-2"
        >
          <LogOut aria-hidden className="h-4 w-4 text-content-tertiary" />
          {loggingOut ? "Signing out…" : "Sign out"}
        </DropdownMenuItem>
        {logoutError ? (
          <p role="alert" className="px-2 py-1 text-caption text-danger-fg">
            Sign out failed. Please try again.
          </p>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Contributes navigation and appearance commands to the ⌘K palette. */
function ShellCommands({
  sections,
}: {
  readonly sections: readonly NavSection[];
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { toggleMode, setDensity } = useTheme();
  const { t } = useTranslation("app_content");

  const actions = useMemo<readonly CommandAction[]>(
    () => [
      ...sections.flatMap((section) =>
        flattenNavItems(section.items).map<CommandAction>((item) => ({
          id: `navigate:${item.href}`,
          label: item.label,
          description: item.href,
          group: "Navigate",
          navigates: true,
          icon: item.icon,
          keywords: [
            section.label ?? "",
            item.href,
            ...(navSearchAliases[stripLanguagePrefix(item.href)] ?? []),
          ],
          perform: () => navigate(withLanguagePrefix(item.href, pathname)),
        })),
      ),
      {
        id: "create:entity",
        navigates: true,
        label: "Create entity",
        description: "Define a new entity for this workspace",
        group: "Create",
        icon: Database,
        perform: () =>
          navigate(withLanguagePrefix("/dashboard/database/new", pathname)),
      },
      {
        id: "assistant:open",
        navigates: true,
        label: t("assistant.open"),
        description: t("assistant.askWorkspace"),
        group: t("assistant.title"),
        icon: Bot,
        perform: () =>
          navigate(withLanguagePrefix("/dashboard/assistant", pathname)),
      },
      {
        id: "theme:toggle",
        label: "Toggle light / dark",
        group: "Appearance",
        icon: Moon,
        perform: toggleMode,
      },
      {
        id: "density:compact",
        label: "Use compact density",
        description: "Fit more rows on screen",
        group: "Appearance",
        icon: Rows3,
        perform: () => setDensity("compact"),
      },
      {
        id: "docs:open",
        label: "Open documentation",
        group: "Help",
        icon: BookOpen,
        perform: () =>
          window.open(brand.urls.docs, "_blank", "noopener,noreferrer"),
      },
    ],
    [sections, navigate, pathname, toggleMode, setDensity, t],
  );

  useRegisterCommands(actions);
  return null;
}

/** Keeps the closest names inside the result cap: "Product" before "ProductVariant". */
function matchRank(definition: GsbEntityDef, needle: string): number {
  return Math.min(
    ...[definition.title, definition.name]
      .filter((name): name is string => Boolean(name))
      .map((name) => {
        const value = name.toLowerCase();
        if (value === needle) return 0;
        if (value.startsWith(needle)) return 1;
        return 2 + value.length / 1000;
      }),
  );
}

/** Resolves entity definitions in the ⌘K palette so any table is one query away. */
function EntityDefCommandSource() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const definitionService = useMemo(() => new EntityDefService(), []);

  const source = useMemo<CommandSource>(
    () => ({
      id: "entity-definitions",
      group: "Tables",
      navigates: true,
      search: async (query, signal) => {
        const needle = query.trim().toLowerCase();
        if (needle.length < 2 || signal.aborted) return [];
        // The definition owner caches successful catalogues. Do not retain a
        // rejected promise here and permanently poison subsequent searches.
        const all = await definitionService.getAllDefinitions();
        if (signal.aborted) return [];
        return all
          .filter((definition) => {
            const name = definition.name ?? "";
            const title = definition.title ?? "";
            return (
              name !== "" &&
              (name.toLowerCase().includes(needle) ||
                title.toLowerCase().includes(needle))
            );
          })
          .sort((a, b) => matchRank(a, needle) - matchRank(b, needle))
          .slice(0, 8)
          .map<CommandAction>((definition) => ({
            id: `table:${definition.name ?? ""}`,
            label: definition.title ?? definition.name ?? "",
            description: `Open ${definition.name ?? ""}`,
            group: "Tables",
            icon: Table2,
            keywords: [definition.name ?? "", "table", "entity", "data"],
            perform: () =>
              navigate(
                withLanguagePrefix(
                  `/dashboard/database/${definition.name ?? ""}`,
                  pathname,
                ),
              ),
          }));
      },
    }),
    [definitionService, navigate, pathname],
  );

  useRegisterCommandSource(source);
  return null;
}

/** Always offers the typed query to the assistant, so the palette answers questions too. */
function useAskAiFallback(): (query: string) => CommandAction {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation("app_content");

  return useCallback(
    (query: string) => ({
      id: "ask-ai",
      navigates: true,
      label: t("assistant.askQuery", { query }),
      description: t("assistant.openQuestion"),
      group: t("assistant.title"),
      icon: Sparkles,
      perform: () =>
        navigate(
          withLanguagePrefix(
            `/dashboard/assistant?q=${encodeURIComponent(query)}`,
            pathname,
          ),
        ),
    }),
    [navigate, pathname, t],
  );
}

export function WorkspaceAppShell({
  shell,
  manifest,
  children,
}: {
  shell: WorkspaceRouteShell;
  manifest: readonly WorkspaceRouteManifestEntry[];
  children: ReactNode;
}) {
  const { t } = useTranslation("app_content");
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [identityOverride, setIdentityOverride] = useState<string>();
  const normalizedPath = stripLanguagePrefix(pathname);
  const localizedPath = (path: string) => withLanguagePrefix(path, pathname);
  const sections = localizeSections(dashboardSections, pathname).map(
    (section) => ({
      ...section,
      items: section.items.map((item) => ({
        ...item,
        label: t(`navigation.${stripLanguagePrefix(item.href)}`, {
          defaultValue: item.label,
        }),
      })),
    }),
  );
  const sectionName = "Workspace";
  const identity =
    identityOverride ??
    (normalizedPath === "/dashboard/assistant"
      ? t("assistant.title")
      : getRouteIdentity(normalizedPath, manifest));
  const language = getLanguage(pathname);
  const askAi = useAskAiFallback();

  return (
    <>
      <Helmet>
        <title>{`${identity} · ${brand.workspaceName}`}</title>
        <html lang={language.code} dir={language.dir} />
      </Helmet>
      <ShellCommands sections={sections} />
      <EntityDefCommandSource />
      <CommandPalette
        fallback={askAi}
        placeholder="Search tables, pages and commands — or ask the assistant…"
      />
      <KeyboardShortcutsDialog />
      <AppShell
        pathname={`${pathname}${search}`}
        sections={sections}
        linkComponent={RouterLink}
        brand={
          <Link
            to={localizedPath("/dashboard")}
            className="flex items-center gap-2 rounded-md font-semibold tracking-tight"
          >
            <BrandMark className="text-primary" size={24} />
            {brand.workspaceName}
          </Link>
        }
        topBarLeading={
          <nav aria-label="Breadcrumb" className="ps-3">
            <ol className="flex items-center gap-2 text-body-sm text-content-tertiary">
              <li>
                <Link
                  to={localizedPath("/dashboard")}
                  className="rounded-sm hover:text-content-primary"
                >
                  {sectionName}
                </Link>
              </li>
              <li aria-hidden>/</li>
              <li
                className="truncate font-medium text-content-primary"
                aria-current="page"
              >
                {identity}
              </li>
            </ol>
          </nav>
        }
        topBarActions={
          <>
            {shell === "dashboard" ? (
              <button
                type="button"
                onClick={() =>
                  void navigate(localizedPath("/dashboard/assistant"))
                }
                aria-label={t("assistant.open")}
                className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-body-sm text-content-secondary hover:bg-surface-hover hover:text-content-primary"
              >
                <Bot aria-hidden className="h-3.5 w-3.5" />
                <span className="hidden lg:inline">{t("assistant.title")}</span>
              </button>
            ) : null}
            <WorkspaceAccountMenu pathname={pathname} />
            <ThemeMenu />
          </>
        }
        sidebarFooter={
          <Link
            to={localizedPath("/dashboard")}
            className="flex min-h-8 items-center rounded-md px-2.5 py-1.5 text-body-sm text-sidebar-foreground hover:bg-surface-hover hover:text-content-primary"
          >
            Workspace
          </Link>
        }
      >
        <RouteIdentityProvider onIdentityChange={setIdentityOverride}>
          {children}
        </RouteIdentityProvider>
      </AppShell>
    </>
  );
}
